import { useMemo } from 'react'

import { cleanUpInbox } from '@/entities/orders/thunks/cleanUpInbox'
import { fetchPlanningOrdersByShift } from '@/entities/orders/thunks/fetchPlanningOrdersByShift'
import { fetchPlanningOrdersByShifts } from '@/entities/orders/thunks/fetchPlanningOrdersByShifts'
import { fetchUnassignedOrdersByScenario } from '@/entities/orders/thunks/fetchUnassignedOrdersByScenario'
import { selectAllScenariosWithShifts } from '@/entities/schedules'
import { useDeleteScenarioMutation } from '@/entities/schedules/apis/planningApi'
import { removeShift } from '@/entities/schedules/thunks/removeShift'
import { showShiftsOnGantt } from '@/entities/widgetWindows/thunks/showShiftsOnGantt'
import { showUnassignedOnMap } from '@/entities/widgetWindows/thunks/showUnassignedOnMap'
import { useMosaicLayoutContext } from '@/fragments/MosaicLayout/MosaicLayoutContext'
import { AddOrderDialog } from '@/fragments/OperationsDialogs/AddOrderDialog/AddOrderDialog'
import { AddRunDialog } from '@/fragments/OperationsDialogs/AddRunDialog/AddRunDialog'
import { AddScenarioDialog } from '@/fragments/OperationsDialogs/AddScenarioDialog/AddScenarioDialog'
import { ConfirmDialog } from '@/fragments/OperationsDialogs/ConfirmDialog/ConfirmDialog'
import { EditShiftDialog } from '@/fragments/OperationsDialogs/EditShiftDialog/EditShiftDialog'
import { ManageDriversDialog } from '@/fragments/OperationsDialogs/ManageDriversDialog/ManageDriversDialog'
import { PublishScenarioDialog } from '@/fragments/OperationsDialogs/PublishedScenarioDialog/PublishScenarioDialog'
import { PublishUnassignedOrdersDialog } from '@/fragments/OperationsDialogs/PublishUnassignedOrdersDialog/PublishUnassignedOrdersDialog'
import { UpdateShiftStartTimeDialog } from '@/fragments/OperationsDialogs/UpdateShiftStartTimeDialog/UpdateShiftStartTimeDialog'
import { CustomTree } from '@/fragments/TreeView/CustomTree'
import { CustomTreeSkeleton } from '@/fragments/TreeView/CustomTreeSkeleton'
import { CustomTreeItemType } from '@/fragments/TreeView/model/CustomTreeItemType'
import { TreeItemAction } from '@/fragments/TreeView/model/TreeItemAction'
import {
  StyledAccordion, StyledAccordionDetails, StyledAccordionSummary, StyledActionIcon, StyledHeaderIcon, StyledHeaderTitle, StyledLoadingContainer,
  StyledLoadingIndicator, StyledSectionHeader, StyledSectionTypography, StyledTreeContainer, StyledTreeHeader, StyledTreeHeaderActions,
  StyledTreeItemIcon,
} from '@/pages/Operations/StyledTreeView'
import { Icon, IconButton } from '@/shared/components'
import { Dropdown } from '@/shared/components/Dropdown/Dropdown'
import { DropdownItem } from '@/shared/components/Dropdown/DropdownItem'
import { GridTableDragType } from '@/shared/components/GridTable/models/GridTableDragType'
import { useDialogContext } from '@/shared/components/Modal/DialogContext'
import { TREE_WIDGETS } from '@/shared/components/Mosaic/models/widgetType'
import { useSubscription } from '@/shared/components/Subscription/SubscriptionContext'
import { SubscriptionUpgradeDialog } from '@/shared/components/Subscription/SubscriptionUpgradeDialog'
import { PLANNING_TREE_SHOW_UTILISATION_PIES_LOCAL_STORAGE_KEY } from '@/shared/config/consts'
import { useLocalStorage } from '@/shared/lib/useLocalStorage'
import { useAppDispatch, useAppSelector } from '@/shared/store'
import { faFileCirclePlus, faTrashCan } from '@fortawesome/pro-light-svg-icons'
import {
  faArrowUpRightFromSquare, faChartPieSimple, faChevronRight, faClock, faEdit, faEllipsisVertical, faEyeSlash, faFilePlus, faFlag, faFolderPlus,
  faPerson, faPlus,
} from '@fortawesome/pro-regular-svg-icons'
import { Box, Divider } from '@mui/material'
import { TreeViewBaseItem } from '@mui/x-tree-view/models'

import { showShiftOnMap } from '../../entities/widgetWindows/thunks/showShiftOnMap'
import { useOperationTreeState } from './libs/useOperationTreeState'

export type PlanningTreeViewProps = {
  //
}

export const PlanningTreeView = () => {
  const { isLoading, isSuccess, isTreeViewLoading, orderInboxCount } = useAppSelector(state => state.planningPage)
  const [deleteScenario] = useDeleteScenarioMutation()
  const [isUtilisationPieShown, setUtilisationPieShown] = useLocalStorage(PLANNING_TREE_SHOW_UTILISATION_PIES_LOCAL_STORAGE_KEY, true)

  const allScenarios = useAppSelector(state => selectAllScenariosWithShifts(state))
  const activeGroupId = useAppSelector(state => state.userInterface.activeGroupId)

  const dispatch = useAppDispatch()

  const { treeState, setTreeState } = useOperationTreeState(activeGroupId, 'planning')
  const { closeWidgetWindows } = useMosaicLayoutContext()
  const { canAccessRunsheet, hasVehicleCapacity } = useSubscription()

  const { openDialog } = useDialogContext()

  // Construct the scenario tree based on the scenarios and their shifts
  const scenarioTree: TreeViewBaseItem<CustomTreeItemType>[] = useMemo(() => {
    const tree = allScenarios?.map(scenario => {
      let children: TreeViewBaseItem<CustomTreeItemType>[] = []

      // Add Unassigned Orders
      const unassignedActions: TreeItemAction[] = [
        {
          type: 'actionItem',
          label: 'Publish Unassigned Orders',
          icon: faFlag,
          action: () => {
            if (!canAccessRunsheet()) {
              openDialog(SubscriptionUpgradeDialog, {
                feature: 'Runsheet Operations',
                reason: 'Runsheet functionality is available on Premium and Enterprise plans.',
              })

              return
            }

            openDialog(PublishUnassignedOrdersDialog, {
              scenarioId: scenario.id,
            })
          },
        },
        {
          type: 'divider',
        },
        {
          type: 'actionItem',
          label: 'Show On Map',
          icon: faArrowUpRightFromSquare,
          action: () => {
            dispatch(fetchUnassignedOrdersByScenario({ scenarioId: scenario.id }))
            dispatch(showUnassignedOnMap({ scheduleId: scenario.id }))
          },
        },
      ]

      const unassignedOrders = {
        id: `${scenario.id}-unassigned`,
        level: 1,
        scenarioId: scenario.id,
        label: `Unassigned (${scenario.unassignedOrderCount})`,
        widgetType: TREE_WIDGETS.ScenarioUnassignedOrders,
        isDraggable: true,
        isDroppable: false,
        acceptDropTypes: [GridTableDragType.SHIFT_STOP_ROW, GridTableDragType.INBOX_ORDER_ROW, GridTableDragType.UNASSIGNED_ORDER_ROW],
        actions: unassignedActions,
      }

      children.push(unassignedOrders)

      // Add Shifts
      const shifts = scenario.shifts.map(shift => {
        const shActions: TreeItemAction[] = [
          {
            type: 'actionItem',
            label: 'Edit Run',
            icon: faEdit,
            action: () => {
              openDialog(EditShiftDialog, {
                shiftId: shift.id,
                formType: 'edit',
                onClose: () => {
                  // Any callback after the dialog is closed
                },
              })
            },
          },
          {
            type: 'actionItem',
            label: 'Update Start Time',
            icon: faClock,
            action: () => {
              openDialog(UpdateShiftStartTimeDialog, {
                scheduleId: scenario.id,
                shiftId: shift.id,
                formType: 'edit',
                onClose: () => {
                  // Any callback after the dialog is closed
                },
              })
            },
          },
          {
            type: 'actionItem',
            label: 'Delete Run',
            icon: faTrashCan,
            action: () => {
              openDialog(ConfirmDialog, {
                title: 'Delete Run',
                message: 'Are you sure you want to delete this shift?',
                onConfirm: () => {
                  dispatch(removeShift({ id: shift.id }))
                  closeWidgetWindows([`${TREE_WIDGETS.PlanningShiftTable}-${shift.id}`])
                },
                confirmText: 'Delete',
              })
            },
          },
          {
            type: 'actionItem',
            label: 'Publish Run',
            icon: faFlag,
            action: () => {
              if (!canAccessRunsheet()) {
                openDialog(SubscriptionUpgradeDialog, {
                  feature: 'Runsheet Operations',
                  reason: 'Runsheet functionality is available on Premium and Enterprise plans.',
                })

                return
              }

              openDialog(PublishScenarioDialog, {
                scenarioId: scenario.id,
                initialRunIds: [shift.runId],
              })
            },
          },
          {
            type: 'divider',
          },
          {
            type: 'actionItem',
            label: 'Show on Map',
            icon: faArrowUpRightFromSquare,
            action: () => {
              dispatch(fetchPlanningOrdersByShift({ shiftId: shift.id }))
              dispatch(showShiftOnMap({ shiftId: shift.id }))
            },
          },
          {
            type: 'actionItem',
            label: 'Show on Gantt',
            icon: faArrowUpRightFromSquare,
            action: () => {
              dispatch(fetchPlanningOrdersByShift({ shiftId: shift.id }))
              dispatch(showShiftsOnGantt({ shiftIds: [shift.id] }))
            },
          },
        ]

        return {
          id: shift.id.toString(),
          level: 1,
          shiftId: shift.id,
          scenarioId: scenario.id,
          violationCount: shift.violationCount,
          label: `${shift.name} (${shift.orderCount})`,
          uiColor: shift.uiColor,
          widgetType: TREE_WIDGETS.PlanningShiftTable,
          isDraggable: true,
          isDroppable: false,
          acceptDropTypes: [
            GridTableDragType.SHIFT_STOP_ROW,
            GridTableDragType.UNASSIGNED_ORDER_ROW,
            GridTableDragType.INBOX_ORDER_ROW],
          actions: shActions,

          // For Utilisation
          showUtilisationPie: isUtilisationPieShown,
          maxVolume: shift.maxVolume,
          maxWeight: shift.maxWeight,
          maxWorkingTimeInMins: shift.maxWorkingTimeInMins,
          totalWorkingTimeInMins: shift.totalWorkingTimeInMins,
          volumeCapacity: shift.volumeCapacity,
          weightCapacity: shift.weightCapacity,
          loads: shift.loads,
        }
      })

      children = children.concat(shifts)

      // Scenario Actions
      const scActions: TreeItemAction[] = [
        {
          type: 'actionItem',
          label: 'Add Run',
          icon: faPlus,
          action: () => {
            if (!hasVehicleCapacity(shifts.length)) {
              openDialog(SubscriptionUpgradeDialog, {
                feature: 'Add Run to Scenario',
                reason: 'You have reached your run limit. Upgrade your plan to add more runs to scenario.',
              })

              return
            }

            openDialog(AddRunDialog, {
              scheduleId: scenario.id,
              onClose: () => {
                // Any callback after the dialog is closed
              },
            })
          },
        },
        {
          type: 'actionItem',
          label: 'Delete Scenario',
          icon: faTrashCan,
          action: () => {
            openDialog(ConfirmDialog, {
              title: 'Delete Scenario',
              message: 'Are you sure you want to delete this scenario?',
              onConfirm: () => {
                deleteScenario(scenario.id)
              },
              confirmText: 'Delete',
            })
          },
        },
        {
          type: 'actionItem',
          label: 'Manage Drivers',
          icon: faPerson,
          action: () => {
            openDialog(ManageDriversDialog, {
              scenarioId: scenario.id,
              onClose: () => {
                // Any callback after the dialog is closed
              },
            })
          },
        },
        {
          type: 'divider',
        },
        {
          type: 'actionItem',
          label: 'Show Shifts on Gantt',
          icon: faArrowUpRightFromSquare,
          action: () => {
            const scenarioShiftIds = scenario.shifts.map(shift => shift.id)

            dispatch(fetchPlanningOrdersByShifts({ shiftIds: scenarioShiftIds }))
            dispatch(showShiftsOnGantt({ shiftIds: scenarioShiftIds }))
          },
        },
        {
          type: 'divider',
        },
        {
          type: 'actionItem',
          label: 'Publish Scenario',
          icon: faFlag,
          action: () => {
            if (!canAccessRunsheet()) {
              openDialog(SubscriptionUpgradeDialog, {
                feature: 'Runsheet Operations',
                reason: 'Runsheet functionality is available on Premium and Enterprise plans.',
              })

              return
            }

            openDialog(PublishScenarioDialog, {
              scenarioId: scenario.id,
            })
          },
        },
      ]

      return {
        id: scenario.id.toString(),
        level: 0,
        scenarioId: scenario.id,
        label: scenario.name,
        violationCount: scenario.violationCount,
        widgetType: TREE_WIDGETS.ScenarioSummaryTable,
        children: children,
        isDraggable: true,
        isDroppable: false,
        acceptDropTypes: [],
        actions: scActions,
      }
    }) || []

    return tree
  }, [allScenarios, isUtilisationPieShown, deleteScenario, dispatch, openDialog, closeWidgetWindows, canAccessRunsheet, hasVehicleCapacity])

  // Construct the order tree for the inbox orders
  const orderTree: TreeViewBaseItem<CustomTreeItemType>[] = useMemo(() => {
    return [
      {
        id: 'inbox',
        level: 0,
        label: `Inbox Orders (${orderInboxCount})`,
        widgetType: TREE_WIDGETS.Inbox,
        isDraggable: true,
        isDroppable: true,
        acceptDropTypes: [GridTableDragType.SHIFT_STOP_ROW, GridTableDragType.UNASSIGNED_ORDER_ROW],
        actions: [],
      },
    ]
  }, [orderInboxCount])

  // Handle the change of expanded items
  const handleExpandedItemsChange = (
    _: React.SyntheticEvent,
    itemIds: string[],
  ) => {
    setTreeState({
      expandedItems: itemIds,
    })
  }

  // Handle adding a new scenario
  const handleAddScenario: React.ReactEventHandler = (ev) => {
    ev.preventDefault()
    ev.stopPropagation()

    openDialog(AddScenarioDialog, {
      onClose: () => {
        // Any callback after the dialog is closed
      },
    })
  }

  // Handle adding a new order
  const handleAddOrder: React.ReactEventHandler = (ev) => {
    ev.preventDefault()
    ev.stopPropagation()

    // Open a dialog to add a new scenario
    openDialog(AddOrderDialog, {
      onClose: () => {
        // Any callback after the dialog is closed
      },
    })
  }

  // Handle cleanup the inbox
  const handleCleanUpInbox = () => {
    openDialog(ConfirmDialog, {
      title: 'Clean Up Inbox',
      message: 'Are you sure you want to clear the inbox?',
      onConfirm: () => {
        dispatch(cleanUpInbox())
      },
      confirmText: 'Clear Inbox',
    })
  }

  return (
    <Box>
      {isLoading && (
        <CustomTreeSkeleton />
      )}
      {isSuccess &&
        <>
          <StyledTreeHeader>
            <StyledSectionTypography variant="h1">
              PLANNING
            </StyledSectionTypography>
            <StyledTreeHeaderActions>
              <IconButton
                tooltipTitle="Add Order"
                onClick={handleAddOrder}
                slotProps={{
                  tooltip: {
                    placement: 'bottom',
                  },
                }}
              >
                <StyledHeaderIcon icon={faFilePlus} />
              </IconButton>
              <IconButton
                tooltipTitle="Add Scenario"
                onClick={handleAddScenario}
                slotProps={{
                  tooltip: {
                    placement: 'bottom',
                  },
                }}
              >
                <StyledHeaderIcon icon={faFolderPlus} />
              </IconButton>
            </StyledTreeHeaderActions>
          </StyledTreeHeader>
          <StyledAccordion
            disableGutters
            defaultExpanded
            aria-controls="planning-order-tree"
            id="planning-order-tree"
          >
            <StyledAccordionSummary
              aria-controls="planning-order-tree-header"
              id="planning-order-tree-header"
            >
              <StyledSectionHeader>
                <StyledHeaderTitle>
                  <StyledTreeItemIcon className="MuiSvgIcon-cheveron" icon={faChevronRight} />
                  <StyledSectionTypography variant="body1">
                    Orders
                  </StyledSectionTypography>
                </StyledHeaderTitle>
                <Box sx={{ display: 'inline-flex', flexDirection: 'row' }}>
                  <Dropdown
                    icon={<StyledActionIcon icon={faEllipsisVertical} />}
                    value="Actions"
                    isIconButton
                    slotProps={{
                      iconButton: {
                        round: true,
                        sx: { margin: 0 },
                      },
                      popper: {
                        placement: 'right-start',
                      },
                    }}
                  >
                    <DropdownItem onSelect={handleAddOrder}>
                      <Icon icon={faFileCirclePlus} />
                      Add Order
                    </DropdownItem>
                    <DropdownItem onSelect={handleCleanUpInbox}>
                      <Icon icon={faTrashCan} />
                      Clear Inbox
                    </DropdownItem>
                  </Dropdown>
                </Box>
              </StyledSectionHeader>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <StyledTreeContainer>
                <CustomTree
                  items={orderTree}
                  defaultExpandedItems={['inbox']}
                  onExpandedItemsChange={handleExpandedItemsChange}
                />
              </StyledTreeContainer>
            </StyledAccordionDetails>
          </StyledAccordion>
          <StyledAccordion
            disableGutters
            defaultExpanded
            aria-controls="planning-scenario-tree"
            id="planning-scenario-tree"
          >
            <StyledAccordionSummary
              aria-controls="planning-scenario-tree-header"
              id="planning-scenario-tree-header"
            >
              <StyledSectionHeader>
                <StyledHeaderTitle>
                  <StyledTreeItemIcon className="MuiSvgIcon-cheveron" icon={faChevronRight} />
                  <StyledSectionTypography variant="body1">
                    Scenarios
                  </StyledSectionTypography>
                </StyledHeaderTitle>
                <Box sx={{ display: 'inline-flex', flexDirection: 'row' }}>
                  <Dropdown
                    icon={<StyledActionIcon icon={faEllipsisVertical} />}
                    value="Actions"
                    isIconButton
                    slotProps={{
                      iconButton: {
                        round: true,
                        sx: { margin: 0 },
                      },
                      popper: {
                        placement: 'right-start',
                      },
                    }}
                  >
                    <DropdownItem onSelect={handleAddScenario}>
                      <Icon icon={faFolderPlus} />
                      Add Scenario
                    </DropdownItem>
                    <Divider />
                    <DropdownItem onSelect={() => { setUtilisationPieShown(prev => !prev) }}>
                      <Icon icon={isUtilisationPieShown ? faEyeSlash : faChartPieSimple} />
                      {isUtilisationPieShown ? 'Hide' : 'Show'} Run Utilisation
                    </DropdownItem>
                  </Dropdown>
                </Box>
              </StyledSectionHeader>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              <StyledTreeContainer>
                <CustomTree
                  items={scenarioTree}
                  defaultExpandedItems={treeState ? treeState.expandedItems : []}
                  onExpandedItemsChange={handleExpandedItemsChange}
                />
              </StyledTreeContainer>
            </StyledAccordionDetails>
          </StyledAccordion>
        </>
      }
      <StyledLoadingContainer>
        {isTreeViewLoading && <StyledLoadingIndicator size={16} />}
      </StyledLoadingContainer>
    </Box>
  )
}
